import { Virtualizer } from "@tanstack/react-virtual";
import { useCallback, useEffect, useRef } from "react";
import { FlatTreeNode } from "~/components/build/Show/routes/list/-components/useTreeNodes";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";

function stickyAncestorHeight(node: FlatTreeNode): number {
  return node.parentPath.reduce((sum, parent) => sum + parent.height, 0);
}

export function useScrollToId(
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>,
  nodes: FlatTreeNode[],
  activeStickyIndexRef: React.RefObject<number[]>,
) {
  const { currentLatestJob, currentStep } = useCurrentJobOrStep();
  const params = useQueryParams();
  const scrollIndexRef = useRef<number>(undefined);

  /**
   * Scroll element into view if it's not currently visible.
   */
  const scrollToId = useCallback(
    (id: string) => {
      // If we've already scrolled to an index, don't scroll again
      if (scrollIndexRef.current !== undefined) {
        return;
      }

      if (!id || !rowVirtualizer?.scrollRect || !rowVirtualizer.scrollElement) {
        return;
      }

      const virtualItem = rowVirtualizer.getVirtualItems().find((item) => item.key === id);
      const scrollTop = rowVirtualizer.scrollElement.scrollTop;
      const containerHeight = rowVirtualizer.scrollRect.height;
      const index = nodes.findIndex((node) => node.id === id);

      if (index === -1) {
        return;
      }

      // Don't scroll if the node is currently rendered as a sticky header,
      // since it's already visible at the top of the scroll container.
      if (activeStickyIndexRef.current?.includes(index)) {
        scrollIndexRef.current = index;
        return;
      }

      const stickyHeight = stickyAncestorHeight(nodes[index]);

      if (
        !virtualItem ||
        virtualItem.start - stickyHeight < scrollTop ||
        virtualItem.end > scrollTop + containerHeight
      ) {
        // Position the item just below its sticky parent headers.
        // We avoid scrollToIndex with align:"start" because it applies
        // a fixed scrollPaddingStart that doesn't match the node's
        // actual sticky header depth.
        const measuredStart = virtualItem?.start ?? index * nodes[index].height;
        const offset = measuredStart - stickyHeight;

        rowVirtualizer.scrollToOffset(offset);
        scrollIndexRef.current = index;
      } else {
        scrollIndexRef.current = virtualItem.index;
      }
    },
    [rowVirtualizer, nodes],
  );

  useEffect(() => {
    // Reset the scroll index ref when the job or step changes
    scrollIndexRef.current = undefined;
  }, [params.jid, params.sid]);

  useEffect(() => {
    // Attempt to scroll to either the currentLatestJob (for jobs
    // within a group step) or currentStep. If the first succeeds the
    // second attempt will return early, but we want to try both,
    // as presence of a currentLatestJob id doesn't necessarily mean
    // there's a corresponding step in the step list.
    if (params.jid && currentLatestJob?.id) {
      scrollToId(currentLatestJob.id);
    }
    if (currentStep?.uuid) {
      scrollToId(currentStep.uuid);
    }
  }, [params.jid, currentLatestJob, currentStep, scrollToId]);
}
